import type { PlatformEvent } from '../types/events';
import type { SubscribeFn } from '../contexts/PlatformEventContext';
import { spaceApi, meApi, ApiError } from '../services/api';
import { TERMINAL_EVENTS } from './run-events-constants';
import { eventDebug } from './event-debug';

/**
 * A 404 from `/runs/{runId}` means the run was never persisted to the runs
 * store (e.g. a warm direct-socket runId that only ever existed over the WS)
 * or has been deleted. Such a run will never reach a terminal status, so
 * polling it is futile — treat it as terminal and stop retrying instead of
 * hammering the endpoint every POLL_INTERVAL_MS forever.
 */
function isRunNotFound(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 404 || err.code === 'NOT_FOUND');
}

export { TERMINAL_EVENTS };

/**
 * Async queue that buffers items and yields them via async iteration.
 * Used to bridge callback-based event subscriptions into async generators.
 */
export class PromiseQueue<T> {
  private queue: T[] = [];
  private waiters: Array<(value: IteratorResult<T>) => void> = [];
  private closed = false;

  push(item: T) {
    if (this.closed) return;
    if (this.waiters.length > 0) {
      this.waiters.shift()!({ value: item, done: false });
    } else {
      this.queue.push(item);
    }
  }

  close() {
    this.closed = true;
    for (const waiter of this.waiters) {
      waiter({ value: undefined as unknown as T, done: true });
    }
    this.waiters = [];
  }

  [Symbol.asyncIterator](): AsyncIterator<T> {
    return {
      next: () => {
        if (this.queue.length > 0) {
          return Promise.resolve({ value: this.queue.shift()!, done: false as const });
        }
        if (this.closed) {
          return Promise.resolve({ value: undefined as unknown as T, done: true as const });
        }
        return new Promise<IteratorResult<T>>((resolve) => {
          this.waiters.push(resolve);
        });
      },
    };
  }
}

/** How long after the last WS event before starting the poll fallback. */
const WS_INACTIVITY_TIMEOUT_MS = 5_000;

const POLL_INTERVAL_MS = 2000;

function makePollEvent(
  runId: string,
  type: 'completed' | 'failed' | 'cancelled',
  data: Record<string, unknown>,
): PlatformEvent {
  return {
    eventId: `poll-${runId}-${type}`,
    eventType: `agent.run.${type}`,
    source: 'poll',
    subjectId: runId,
    data,
    metadata: { timestamp: new Date().toISOString() },
    orgId: '',
  };
}

/**
 * Grace period (ms) after a terminal event before closing the stream queue.
 * Allows late-arriving events (e.g. an `ask_user_requested` or
 * `final_answer_stop` that lands just after `completed`) to still be
 * processed. Applies to BOTH WS-delivered and poll-synthesized terminals —
 * a poll-completed that wins the race against the WS ask event must not slam
 * the door on it.
 */
const TERMINAL_GRACE_MS = 1000;

/**
 * Best-effort parked-run detection: the runtime parks an ask_user run by
 * marking it `completed`, so a poll that discovers `completed` cannot tell a
 * finished run from a parked one. Check the personal inbox for a pending
 * ask_user item belonging to this run and synthesize the
 * `ask_user_requested` event the WS would have delivered. Returns null on any
 * failure or when the run isn't parked — callers degrade to plain completed.
 *
 * A stale-`pending` item left behind by a failed WS answer closeout can
 * briefly resurface here too — that self-heals within ~12s via
 * `verifyAskUserResolution` (utils/ask-user-verify.ts), whose REST fallback
 * resolves the request and emits `inbox.item_resolved`.
 */
async function fetchPendingAskEvent(runId: string): Promise<PlatformEvent | null> {
  try {
    const { items } = await meApi.inbox({
      status: 'pending',
      refType: 'ask_user',
      limit: 30,
    });
    const item = items.find((i) => i.runId === runId);
    if (!item) return null;
    // The list row carries ids but not the questions — read the item with the
    // expanded AskUserRequest (same call the inline card itself makes).
    const full = await meApi.inboxItem(item.inboxItemId, { expand: 'source' });
    const source = full.source;
    if (!source?.questions?.length || (source.status ?? 'pending') !== 'pending') {
      return null;
    }
    eventDebug.log('poll', 'parked run detected — synthesizing ask_user_requested', { runId });
    return {
      eventId: `poll-${runId}-ask_user_requested`,
      eventType: 'agent.run.ask_user_requested',
      source: 'poll',
      subjectId: runId,
      spaceId: item.spaceId ?? undefined,
      data: {
        runId,
        askUserRequestId: item.refId,
        inboxItemId: item.inboxItemId,
        conversationId: item.conversationId,
        agentId: item.agentId,
        agentName: item.agentName,
        questions: source.questions,
      },
      metadata: { timestamp: new Date().toISOString() },
      orgId: '',
    };
  } catch {
    return null;
  }
}

/**
 * Creates an async generator that yields PlatformEvents for a specific run
 * using WebSocket subscription with an inactivity-based polling fallback.
 *
 * The WS stream provides low-latency progress events. If no WS event arrives
 * within WS_INACTIVITY_TIMEOUT_MS, a polling loop starts to catch terminal
 * events via HTTP. If WS resumes while polling, polling stops and the
 * inactivity timer restarts.
 */
export interface RunStreamOptions {
  includeSubagents?: boolean;
  /** Events buffered before the stream was created (e.g. during invokeAsync). */
  preBufferedEvents?: PlatformEvent[];
  /** Resolves the run's owning space at poll time. Required for the HTTP
   *  fallback (`/spaces/{spaceId}/runs/{runId}`). When unavailable, polling
   *  is skipped and we rely entirely on the WS stream. */
  getSpaceId?: () => string | undefined;
}

export async function* createRunStream(
  subscribe: SubscribeFn,
  runId: string,
  signal: AbortSignal,
  options?: RunStreamOptions,
): AsyncGenerator<PlatformEvent> {
  const queue = new PromiseQueue<PlatformEvent>();
  let receivedTerminal = false;
  let pollAborted = false;
  let wsActive = false;
  let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

  // --- Inactivity-based poll scheduling ---
  const scheduleInactivityPoll = () => {
    if (inactivityTimer) clearTimeout(inactivityTimer);
    if (receivedTerminal || pollAborted || signal.aborted) return;
    inactivityTimer = setTimeout(() => {
      if (!receivedTerminal && !pollAborted && !signal.aborted) {
        startPoll();
      }
    }, WS_INACTIVITY_TIMEOUT_MS);
  };

  const startPoll = async () => {
    eventDebug.recordPollStart(runId);
    while (!receivedTerminal && !pollAborted && !signal.aborted) {
      const pollSpaceId = options?.getSpaceId?.();
      if (!pollSpaceId) {
        // No space resolver available — give up on polling. The WS stream
        // will continue to drive completion if it's still connected.
        return;
      }
      // Always poll once before checking wsActive — catches fast-failing runs
      // where the terminal WS event was lost but the DB already has the result.
      try {
        const status = await spaceApi(pollSpaceId).runs.get(runId);
        if (receivedTerminal || signal.aborted) return;

        if (status.status === 'completed') {
          receivedTerminal = true;
          eventDebug.recordTerminal(runId, 'agent.run.completed', 'poll');
          // The runtime parks ask_user runs as `completed` — probe for a
          // pending ask so the question card renders even on poll-only paths.
          const askEvent = await fetchPendingAskEvent(runId);
          if (askEvent) queue.push(askEvent);
          queue.push(
            makePollEvent(runId, 'completed', {
              response: status.response ?? '',
              steps: status.steps,
              durationMs: status.durationMs ?? 0,
            }),
          );
          // Grace-close (not synchronous): a WS ask_user_requested that lost
          // the race to this poll can still land and flip the terminal back.
          setTimeout(() => { doUnsubscribe(); queue.close(); }, TERMINAL_GRACE_MS);
          return;
        }

        // Out of iteration budget: a non-error terminal. Synthesized as
        // `completed` + the incomplete markers, exactly as the runtime emits
        // it over WS — without this branch the poller never sees a terminal
        // status it recognizes and spins until the run TTLs out.
        if (status.status === 'incomplete') {
          receivedTerminal = true;
          eventDebug.recordTerminal(runId, 'agent.run.completed', 'poll');
          queue.push(
            makePollEvent(runId, 'completed', {
              response: status.response ?? '',
              steps: status.steps,
              durationMs: status.durationMs ?? 0,
              incomplete: true,
              stopReason: status.stopReason,
              resumable: status.resumable,
            }),
          );
          setTimeout(() => { doUnsubscribe(); queue.close(); }, TERMINAL_GRACE_MS);
          return;
        }

        if (status.status === 'failed' || status.status === 'error') {
          receivedTerminal = true;
          eventDebug.recordTerminal(runId, 'agent.run.failed', 'poll');
          queue.push(
            makePollEvent(runId, 'failed', {
              error: status.error ?? 'Run failed',
              code: status.errorCode,
            }),
          );
          setTimeout(() => { doUnsubscribe(); queue.close(); }, TERMINAL_GRACE_MS);
          return;
        }

        if (status.status === 'cancelled') {
          receivedTerminal = true;
          eventDebug.recordTerminal(runId, 'agent.run.cancelled', 'poll');
          queue.push(
            makePollEvent(runId, 'cancelled', {
              cancelledAt: status.completedAt ?? new Date().toISOString(),
            }),
          );
          setTimeout(() => { doUnsubscribe(); queue.close(); }, TERMINAL_GRACE_MS);
          return;
        }
      } catch (err) {
        if (isRunNotFound(err)) {
          // Run isn't in the runs store — stop polling (no terminal will come).
          eventDebug.log('poll', 'run not found — stopping poll', { runId });
          return;
        }
        // ignore transient poll errors — will retry
      }

      // If WS resumed since last check, stop polling and restart inactivity timer
      if (wsActive) {
        wsActive = false;
        scheduleInactivityPoll();
        return;
      }

      if (!receivedTerminal && !pollAborted && !signal.aborted) {
        await new Promise<void>((resolve) => {
          const t = setTimeout(resolve, POLL_INTERVAL_MS);
          signal.addEventListener('abort', () => { clearTimeout(t); resolve(); }, { once: true });
        });
      }
    }
  };

  // --- WS subscription ---
  let unsubscribed = false;
  const doUnsubscribe = () => { if (!unsubscribed) { unsubscribed = true; unsub(); } };

  const unsub = subscribe(
    [{ eventType: 'agent.run.*', runId, includeSubagents: options?.includeSubagents }],
    (event) => {
      // After terminal, still accept non-terminal events during grace period
      if (receivedTerminal && TERMINAL_EVENTS.has(event.eventType)) return;
      wsActive = true;
      scheduleInactivityPoll();
      queue.push(event);
      eventDebug.recordRunEvent(runId, event.eventType);
      // Only treat terminal events from the coordinator (no parentRunId) as stream-closing
      const data = event.data as Record<string, unknown>;
      if (TERMINAL_EVENTS.has(event.eventType) && !data?.parentRunId) {
        receivedTerminal = true;
        eventDebug.recordTerminal(runId, event.eventType, 'ws');
        // Delay closing the queue to drain late-arriving events
        setTimeout(() => queue.close(), TERMINAL_GRACE_MS);
      }
    },
  );

  // Drain any pre-buffered events (captured before the stream was created).
  // The early subscription is necessarily un-scoped (runId isn't known yet),
  // so filter to events that belong to this run — otherwise concurrent runs
  // from other conversations leak into our reducer state and CoT timeline.
  if (options?.preBufferedEvents) {
    let preMatched = 0;
    eventDebug.log('prebuffer', `draining ${options.preBufferedEvents.length} early event(s)`, {
      runId,
    });
    for (const event of options.preBufferedEvents) {
      const data = event.data as Record<string, unknown>;
      const eventRunId = event.subjectId ?? (data?.runId as string | undefined);
      const matchesRun =
        eventRunId === runId ||
        (options.includeSubagents === true && data?.parentRunId === runId);
      if (!matchesRun) continue;
      preMatched += 1;

      if (receivedTerminal && TERMINAL_EVENTS.has(event.eventType)) continue;
      wsActive = true;
      queue.push(event);
      eventDebug.recordRunEvent(runId, event.eventType);
      if (TERMINAL_EVENTS.has(event.eventType) && !data?.parentRunId) {
        receivedTerminal = true;
        eventDebug.recordTerminal(runId, event.eventType, 'ws');
        setTimeout(() => queue.close(), TERMINAL_GRACE_MS);
      }
    }
    eventDebug.log('prebuffer', `${preMatched}/${options.preBufferedEvents.length} matched this run`, {
      runId,
    });
  }

  // Start initial inactivity timer — if no WS event arrives within the
  // timeout, polling will kick in automatically.
  scheduleInactivityPoll();

  // --- Abort handling ---
  const onAbort = () => {
    pollAborted = true;
    if (inactivityTimer) clearTimeout(inactivityTimer);
    doUnsubscribe();
    queue.close();
  };
  signal.addEventListener('abort', onAbort, { once: true });

  try {
    for await (const event of queue) {
      if (signal.aborted) return;
      yield event;
    }
  } finally {
    pollAborted = true;
    if (inactivityTimer) clearTimeout(inactivityTimer);
    signal.removeEventListener('abort', onAbort);
    doUnsubscribe();
  }
}

// ─── Direct-socket coexistence: merge + dedupe ───────────────────────────────

/**
 * Event types the direct per-run WebSocket authoritatively delivers for the
 * PRIMARY run it was opened for. When the direct socket is the source of truth
 * for a run, fan-out copies of these (whose effective runId === the primary
 * runId) are dropped to avoid double-rendering. Everything NOT in this set —
 * sub-agent progress (own subjectId / `parentRunId`), sub-agent lifecycle,
 * `ask_user`, delegated child runs — keeps flowing from fan-out, which remains
 * the only transport for them.
 *
 * NOTE: verify `subagent_*` lifecycle tagging against the live backend; if any
 * of those turn out to be socket-delivered, add them here.
 */
export const SOCKET_OWNED_PRIMARY_EVENTS = new Set<string>([
  'agent.run.started',
  'agent.run.completed',
  'agent.run.failed',
  'agent.run.cancelled',
  'agent.run.reasoning_start',
  'agent.run.reasoning_delta',
  'agent.run.reasoning_stop',
  'agent.run.response_start',
  'agent.run.response_delta',
  'agent.run.response_stop',
  'agent.run.final_answer_start',
  'agent.run.final_answer_delta',
  'agent.run.final_answer_stop',
  'agent.run.tool_call',
  'agent.run.tool_result',
  'agent.run.skill_activated',
  'agent.run.skill_deactivated',
  'agent.run.canvas_created',
  'agent.run.surface_update',
  'agent.run.surface_deleted',
  'agent.run.surface_moved',
  'agent.run.context_compaction_start',
  'agent.run.context_compaction_stop',
  // Voice sessions live entirely on the direct socket (the session store is
  // the sole consumer); fan-out mirrors must never double-process them in the
  // surface that owns the socket.
  'agent.run.voice_session_started',
  'agent.run.voice_session_stopped',
  'agent.run.voice_transcript',
  'agent.run.voice_transcript_delta',
]);

/**
 * Drops fan-out events the direct socket already owns for `primaryRunId`, so
 * the two transports can run in parallel without double-rendering the primary
 * run. An event is owned (and dropped) iff its effective runId equals the
 * primary runId AND its type is in {@link SOCKET_OWNED_PRIMARY_EVENTS}.
 */
export async function* filterPrimary(
  stream: AsyncGenerator<PlatformEvent>,
  primaryRunId: string,
): AsyncGenerator<PlatformEvent> {
  for await (const event of stream) {
    const data = event.data as Record<string, unknown>;
    const effectiveRunId = event.subjectId ?? (data?.runId as string | undefined);
    const ownedBySocket =
      effectiveRunId === primaryRunId && SOCKET_OWNED_PRIMARY_EVENTS.has(event.eventType);
    if (ownedBySocket) continue;
    yield event;
  }
}

/**
 * Interleaves PlatformEvent streams into one, yielding events as they arrive.
 * The FIRST stream is the LEAD: when it completes the merge completes and the
 * remaining streams are `.return()`-ed (so e.g. a fan-out `createRunStream`
 * unsubscribes via its `finally`). This makes the direct socket authoritative
 * for the run's lifetime — when it ends (terminal OR socket death), the merge
 * ends and the caller can decide whether a fallback pass is needed.
 *
 * The lead being authoritative is sound because a coordinator run only reaches
 * its terminal event after its sub-agents have returned, so abandoning the
 * secondary (sub-agent) fan-out stream at lead-end drops nothing live.
 *
 * A stream that throws is treated as ended (error swallowed) so one transport
 * failing never stalls the others.
 */
export async function* mergeRunStreams(
  lead: AsyncGenerator<PlatformEvent>,
  ...rest: AsyncGenerator<PlatformEvent>[]
): AsyncGenerator<PlatformEvent> {
  const queue = new PromiseQueue<PlatformEvent>();

  const pump = async (stream: AsyncGenerator<PlatformEvent>, isLead: boolean) => {
    try {
      for await (const event of stream) queue.push(event);
    } catch {
      // A failing source ends quietly.
    } finally {
      if (isLead) {
        // Lead done → close the merge and release the secondary streams.
        queue.close();
        for (const s of rest) void s.return?.(undefined);
      }
    }
  };

  void pump(lead, true);
  for (const stream of rest) void pump(stream, false);

  for await (const event of queue) yield event;
}

/**
 * Polling-only fallback when WebSocket is unavailable. Polls
 * `/spaces/{spaceId}/runs/{runId}` and converts statuses into
 * PlatformEvent-shaped objects.
 */
export async function* pollRunStatus(
  runId: string,
  signal: AbortSignal,
  getSpaceId: () => string | undefined,
): AsyncGenerator<PlatformEvent> {
  eventDebug.recordPollStart(runId);
  while (!signal.aborted) {
    try {
      const pollSpaceId = getSpaceId();
      if (!pollSpaceId) return;
      const status = await spaceApi(pollSpaceId).runs.get(runId);

      if (status.status === 'completed') {
        eventDebug.recordTerminal(runId, 'agent.run.completed', 'poll');
        // Parked ask_user runs report `completed` — probe for a pending ask
        // and replay it first so the reducer parks instead of finalizing.
        const askEvent = await fetchPendingAskEvent(runId);
        if (askEvent) yield askEvent;
        yield makePollEvent(runId, 'completed', {
          response: status.response ?? '',
          conversationId: status.conversationId ?? '',
          steps: status.steps,
          usage: { inputTokens: 0, outputTokens: 0 },
          durationMs: status.durationMs ?? 0,
        });
        return;
      }

      // See the equivalent branch in the backstop poller above.
      if (status.status === 'incomplete') {
        eventDebug.recordTerminal(runId, 'agent.run.completed', 'poll');
        yield makePollEvent(runId, 'completed', {
          response: status.response ?? '',
          conversationId: status.conversationId ?? '',
          steps: status.steps,
          usage: { inputTokens: 0, outputTokens: 0 },
          durationMs: status.durationMs ?? 0,
          incomplete: true,
          stopReason: status.stopReason,
          resumable: status.resumable,
        });
        return;
      }

      if (status.status === 'failed' || status.status === 'error') {
        yield makePollEvent(runId, 'failed', {
          error: status.error ?? 'Run failed',
          code: status.errorCode,
        });
        return;
      }

      if (status.status === 'cancelled') {
        yield makePollEvent(runId, 'cancelled', {
          cancelledAt: status.completedAt ?? new Date().toISOString(),
        });
        return;
      }

      // Still running — wait and poll again
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, POLL_INTERVAL_MS);
        signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
      });
    } catch (err) {
      if (signal.aborted) return;
      if (isRunNotFound(err)) {
        // Run isn't in the runs store — stop polling (no terminal will come).
        eventDebug.log('poll', 'run not found — stopping poll', { runId });
        return;
      }
      // On transient error, wait and retry
      await new Promise<void>((resolve) => {
        const timer = setTimeout(resolve, POLL_INTERVAL_MS);
        signal.addEventListener('abort', () => { clearTimeout(timer); resolve(); }, { once: true });
      });
    }
  }
}
